import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import type { PadButton, SystemId } from '../emulator/types';
import type { InputManager } from '../input/InputManager';
import { useMultiTouch, type StickState, type TouchOutput } from '../input/useMultiTouch';
import {
  PORTRAIT_CHASSIS_CLASS,
  resolveLayout,
  zonesFor,
  type DirectionalStyle,
  type PadLayout,
  type ZoneDef,
} from './padLayout';

export interface VirtualPadProps {
  system: SystemId;
  input: InputManager;
  /** PS1 のアナログスティックを表示するか */
  analog?: boolean;
  opacity?: number;
  scale?: number;
  orientation?: 'portrait' | 'landscape';
  /** ユーザーが編集した配置。未指定なら既定配置。 */
  layout?: PadLayout | undefined;
  /** 方向入力の種類（十字キー / スティック / フローティングスティック） */
  directionalStyle?: DirectionalStyle;
  onMenu?: () => void;
  /** 巻き戻しボタン。押している間 true が流れる。 */
  onRewind?: (active: boolean) => void;
  /** 早送り（最大速度）ボタン。押している間 true が流れる。 */
  onFastForward?: (active: boolean) => void;
  /** 現在の再生速度（表示用）。未指定なら速度ボタンを出さない。 */
  speed?: number;
  /** 速度を 1 段階遅くする */
  onSpeedDown?: () => void;
  /** 速度を 1 段階速くする */
  onSpeedUp?: () => void;
}

/** 仮想パッドの速度ボタンとポーズメニューで共有する範囲 */
export const PAD_SPEED_MIN = 0.5;
export const PAD_SPEED_MAX = 4;
export const PAD_SPEED_STEP = 0.5;

export function clampPadSpeed(value: number): number {
  const stepped = Math.round(value / PAD_SPEED_STEP) * PAD_SPEED_STEP;
  return Math.min(PAD_SPEED_MAX, Math.max(PAD_SPEED_MIN, Number(stepped.toFixed(1))));
}

const DIRECTIONS: PadButton[] = ['UP', 'DOWN', 'LEFT', 'RIGHT'];

/**
 * iPhone 向けバーチャルパッド。
 *
 * 縦持ちは機種ごとの実機風スキン、横持ちは共通の汎用パッド。
 */
export function VirtualPad({
  system,
  input,
  analog = false,
  opacity = 0.55,
  scale = 1,
  orientation = 'portrait',
  layout,
  directionalStyle = 'dpad',
  onMenu,
  onRewind,
  onFastForward,
  speed,
  onSpeedDown,
  onSpeedUp,
}: VirtualPadProps) {
  const [pressed, setPressed] = useState<Set<PadButton>>(new Set());
  const [sticks, setSticks] = useState<StickState[]>([]);
  const pressedRef = useRef(pressed);
  const hardwareSkin = orientation === 'portrait';

  const zones = useMemo(
    () => zonesFor(system, { analog, directionalStyle }),
    [system, analog, directionalStyle],
  );
  const buttonZones = useMemo(() => zones.filter((z) => z.button), [zones]);

  const handleChange = useCallback(
    (out: TouchOutput) => {
      const prev = pressedRef.current;

      input.setTouchDirections({
        up: out.buttons.has('UP'),
        down: out.buttons.has('DOWN'),
        left: out.buttons.has('LEFT'),
        right: out.buttons.has('RIGHT'),
      });

      for (const zone of buttonZones) {
        const b = zone.button!;
        if (DIRECTIONS.includes(b)) continue;
        const now = out.buttons.has(b);
        if (now !== prev.has(b)) input.setTouchButton(b, now);
      }

      // アナログ軸として機種へ渡すのは PS1/PSP 用のスティックだけ。
      // 方向キー代わりのスティックはボタンとして扱う（機種側に軸が無い）。
      const analogStick = out.sticks.find((s) => s.zoneId === 'stick-left');
      if (analogStick) input.setTouchAnalog('left', analogStick.x, analogStick.y);
      else input.setTouchAnalog('left', 0, 0);

      pressedRef.current = out.buttons;
      setPressed(new Set(out.buttons));
      setSticks(out.sticks);
    },
    [input, buttonZones],
  );

  const { rootRef, register, measureAll } = useMultiTouch(handleChange);

  useEffect(() => {
    const id = requestAnimationFrame(measureAll);
    return () => cancelAnimationFrame(id);
  }, [system, analog, orientation, layout, directionalStyle, measureAll]);

  useEffect(() => () => input.releaseTouch(), [input]);

  return (
    <div
      ref={rootRef}
      className="pointer-events-auto absolute inset-0 z-20 select-none"
      style={
        {
          opacity,
          touchAction: 'none',
          WebkitUserSelect: 'none',
          WebkitTouchCallout: 'none',
          WebkitTapHighlightColor: 'transparent',
          '--pad-scale': scale,
        } as CSSProperties
      }
    >
      {hardwareSkin ? (
        // 縦持ち: ボタンと速度 HUD をパッド枠の子にして、枠外にはみ出さない
        <div className={PORTRAIT_CHASSIS_CLASS}>
          <ChassisDecor system={system} />
          {zones.map((zone) => (
            <PadZone
              key={zone.id}
              zone={zone}
              system={system}
              orientation={orientation}
              layout={layout}
              register={register}
              pressed={pressed}
              stick={sticks.find((s) => s.zoneId === zone.id)}
              hardwareSkin={hardwareSkin}
            />
          ))}
          {onSpeedDown && onSpeedUp && (
            <div className="absolute left-1/2 top-[6%] z-30 flex -translate-x-1/2 items-center gap-1.5">
              <HudButton
                label="速度を下げる"
                disabled={speed !== undefined && speed <= PAD_SPEED_MIN}
                onPress={onSpeedDown}
              >
                <IconSpeedDown />
              </HudButton>
              <span
                className="min-w-[2.75rem] rounded-full border border-white/15 bg-black/55 px-2 py-2 text-center font-mono text-[0.65rem] font-medium tabular-nums text-white/75"
                aria-live="polite"
              >
                {(speed ?? 1).toFixed(1)}x
              </span>
              <HudButton
                label="速度を上げる"
                disabled={speed !== undefined && speed >= PAD_SPEED_MAX}
                onPress={onSpeedUp}
              >
                <IconSpeedUp />
              </HudButton>
              {onFastForward && (
                <HoldHudButton label="早送り" onHold={onFastForward}>
                  <IconFastForward />
                </HoldHudButton>
              )}
            </div>
          )}
        </div>
      ) : (
        <>
          {zones.map((zone) => (
            <PadZone
              key={zone.id}
              zone={zone}
              system={system}
              orientation={orientation}
              layout={layout}
              register={register}
              pressed={pressed}
              stick={sticks.find((s) => s.zoneId === zone.id)}
              hardwareSkin={hardwareSkin}
            />
          ))}
          {onSpeedDown && onSpeedUp && (
            <div className="top-safe-hud absolute left-1/2 z-30 flex -translate-x-1/2 items-center gap-1.5">
              <HudButton
                label="速度を下げる"
                disabled={speed !== undefined && speed <= PAD_SPEED_MIN}
                onPress={onSpeedDown}
              >
                <IconSpeedDown />
              </HudButton>
              <span
                className="min-w-[2.75rem] rounded-full border border-white/15 bg-black/55 px-2 py-2 text-center font-mono text-[0.65rem] font-medium tabular-nums text-white/75"
                aria-live="polite"
              >
                {(speed ?? 1).toFixed(1)}x
              </span>
              <HudButton
                label="速度を上げる"
                disabled={speed !== undefined && speed >= PAD_SPEED_MAX}
                onPress={onSpeedUp}
              >
                <IconSpeedUp />
              </HudButton>
            </div>
          )}
        </>
      )}

      <div className="top-safe-hud absolute right-[max(0.75rem,env(safe-area-inset-right))] flex items-center gap-1.5">
        {!hardwareSkin && onFastForward && (
          <HoldHudButton label="早送り" onHold={onFastForward}>
            <IconFastForward />
          </HoldHudButton>
        )}
        {onRewind && (
          <HoldHudButton label="巻き戻す" onHold={onRewind}>
            <IconRewind />
          </HoldHudButton>
        )}
        {onMenu && (
          <button
            type="button"
            onPointerDown={(e) => {
              e.stopPropagation();
              onMenu();
            }}
            className="inline-flex h-9 min-w-9 items-center justify-center rounded-full border border-white/20 bg-black/55 px-2.5 text-white/80"
            aria-label="メニューを開く"
          >
            <IconMenu />
          </button>
        )}
      </div>
    </div>
  );
}

function IconFastForward() {
  // icons8 系の角丸ソリッド二重三角。currentColor で HUD の文字色に合わせる。
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden fill="currentColor">
      <path d="M3.2 6.4c0-.85.95-1.35 1.65-.85l8.1 5.1c.65.4.65 1.35 0 1.75l-8.1 5.1c-.7.45-1.65-.05-1.65-.9V6.4z" />
      <path d="M12.1 6.4c0-.85.95-1.35 1.65-.85l8.1 5.1c.65.4.65 1.35 0 1.75l-8.1 5.1c-.7.45-1.65-.05-1.65-.9V6.4z" />
    </svg>
  );
}

function IconRewind() {
  return (
    <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" aria-hidden fill="currentColor">
      <path d="M20.4 6.2v11.6L12 12l8.4-5.8zM11.6 6.2v11.6L3.2 12l8.4-5.8z" />
    </svg>
  );
}

function IconMenu() {
  return (
    <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" aria-hidden fill="currentColor">
      <path d="M4 7h16v1.8H4V7zm0 4.1h16v1.8H4v-1.8zm0 4.1h16V17H4v-1.8z" />
    </svg>
  );
}

function HoldHudButton({
  label,
  onHold,
  children,
}: {
  label: string;
  onHold: (active: boolean) => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onPointerDown={(e) => {
        e.stopPropagation();
        e.currentTarget.setPointerCapture(e.pointerId);
        onHold(true);
      }}
      onPointerUp={(e) => {
        e.stopPropagation();
        onHold(false);
      }}
      onPointerCancel={() => onHold(false)}
      onLostPointerCapture={() => onHold(false)}
      className="inline-flex h-9 min-w-9 items-center justify-center rounded-full border border-white/20 bg-black/55 px-2.5 text-white/80 transition-colors active:border-accent active:bg-accent/60 active:text-black"
      aria-label={label}
    >
      {children}
    </button>
  );
}

function IconSpeedDown() {
  return (
    <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" aria-hidden fill="currentColor">
      <path d="M5 11h14v2.2H5V11z" />
    </svg>
  );
}

function IconSpeedUp() {
  return (
    <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" aria-hidden fill="currentColor">
      <path d="M11 5h2.2v14H11V5zm-6 6h14v2.2H5V11z" />
    </svg>
  );
}

function HudButton({
  label,
  onPress,
  disabled,
  children,
}: {
  label: string;
  onPress: () => void;
  disabled?: boolean;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onPointerDown={(e) => {
        e.stopPropagation();
        if (disabled) return;
        onPress();
      }}
      className="inline-flex h-9 min-w-9 items-center justify-center rounded-full border border-white/20 bg-black/55 px-2.5 text-white/80 transition-colors active:border-accent active:bg-accent/60 active:text-black disabled:opacity-35"
      aria-label={label}
    >
      {children}
    </button>
  );
}

/* -------------------------------------------------------------------------- */
/* 縦持ちのボディ装飾                                                          */
/* -------------------------------------------------------------------------- */

function ChassisDecor({ system }: { system: SystemId }) {
  const tone =
    system === 'gb'
      ? 'from-[#8b956d]/25 to-[#5a6340]/10'
      : system === 'gba'
        ? 'from-[#5b4a9c]/25 to-[#2e2456]/10'
        : system === 'nes'
        ? 'from-[#c8c8c8]/20 to-[#6a6a6a]/10'
        : system === 'snes'
          ? 'from-[#7a6aa8]/25 to-[#3d3558]/10'
          : system === 'ws'
            ? 'from-[#d4c4a8]/25 to-[#8a7a60]/10'
            : system === 'psp'
              ? 'from-[#1a237e]/30 to-[#0d1333]/15'
              : 'from-[#2a2a2a]/40 to-[#111]/20';

  return (
    <div
      aria-hidden
      className={`pointer-events-none absolute inset-0 rounded-[2rem] border border-white/10 bg-gradient-to-b ${tone}`}
    />
  );
}

/* -------------------------------------------------------------------------- */
/* ゾーン                                                                      */
/* -------------------------------------------------------------------------- */

type RegisterFn = ReturnType<typeof useMultiTouch>['register'];

export function zoneStyle(
  zone: ZoneDef,
  orientation: 'portrait' | 'landscape',
  layout: PadLayout | undefined,
): React.CSSProperties {
  const pos = resolveLayout(zone, orientation, layout);
  // 縦持ちはパッド枠の子なので size は枠幅％。横持ちは画面 vmin。
  const unit = orientation === 'portrait' ? '%' : 'vmin';
  const size =
    orientation === 'portrait' ? (zone.portraitSize ?? zone.size) : zone.size;
  const heightRatio =
    orientation === 'portrait'
      ? (zone.portraitHeightRatio ?? zone.heightRatio)
      : zone.heightRatio;
  const w = `calc(${size}${unit} * var(--pad-scale) * ${pos.scale})`;
  const h = heightRatio
    ? `calc(${size * heightRatio}${unit} * var(--pad-scale) * ${pos.scale})`
    : w;

  return {
    position: 'absolute',
    left: `${pos.x * 100}%`,
    top: `${pos.y * 100}%`,
    transform: 'translate(-50%, -50%)',
    width: w,
    height: h,
    // 縦持ちは枠内％を優先。固定 min があると枠上端にはみ出す。
    minHeight: orientation === 'landscape' && heightRatio ? '1.5rem' : undefined,
  };
}

function PadZone({
  zone,
  system,
  orientation,
  layout,
  register,
  pressed,
  stick,
  hardwareSkin,
}: {
  zone: ZoneDef;
  system: SystemId;
  orientation: 'portrait' | 'landscape';
  layout: PadLayout | undefined;
  register: RegisterFn;
  pressed: Set<PadButton>;
  stick?: StickState | undefined;
  hardwareSkin: boolean;
}) {
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    return register({
      id: zone.id,
      kind: zone.kind,
      el,
      slop: zone.slop,
      ...(zone.button ? { button: zone.button } : {}),
      ...(zone.asDpad ? { asDpad: true } : {}),
      ...(zone.floating ? { floating: true, radius: stickRadiusPx() } : {}),
    });
  }, [zone, register]);

  return (
    <div
      ref={ref}
      data-zone={zone.id}
      style={zoneStyle(zone, orientation, layout)}
      aria-hidden
    >
      <ZoneVisual
        zone={zone}
        pressed={pressed}
        stick={stick}
        system={system}
        hardwareSkin={hardwareSkin}
      />
    </div>
  );
}

/**
 * フローティングスティックで「最大まで倒した」とみなす半径。
 * 画面の短辺に対する比率で決める。小さすぎると指が浅くても最大入力になり、
 * 大きすぎると端まで倒しきれない。
 */
function stickRadiusPx(): number {
  if (typeof window === 'undefined') return 60;
  return Math.max(40, Math.min(window.innerWidth, window.innerHeight) * 0.13);
}

export function ZoneVisual({
  zone,
  pressed,
  stick,
  system = 'snes',
  hardwareSkin = false,
}: {
  zone: ZoneDef;
  pressed: Set<PadButton>;
  stick?: StickState | undefined;
  system?: SystemId;
  hardwareSkin?: boolean;
}) {
  if (zone.kind === 'dpad') {
    return <DPadVisual pressed={pressed} system={system} hardwareSkin={hardwareSkin} />;
  }
  if (zone.kind === 'stick') return <StickVisual zone={zone} stick={stick} pressed={pressed} />;

  const active = zone.button ? pressed.has(zone.button) : false;
  if (hardwareSkin) {
    return <HardwareButton zone={zone} system={system} active={active} />;
  }

  const pill = Boolean(zone.heightRatio);
  return (
    <div
      className={`flex h-full w-full items-center justify-center border transition-colors ${
        pill ? 'rounded-xl text-[0.65rem] font-bold tracking-widest' : 'rounded-full text-lg font-bold'
      } ${active ? 'border-accent bg-accent/70 text-black' : 'border-white/20 bg-black/55 text-white/70'}`}
    >
      {zone.label}
    </div>
  );
}

function HardwareButton({
  zone,
  system,
  active,
}: {
  zone: ZoneDef;
  system: SystemId;
  active: boolean;
}) {
  const pill = Boolean(zone.heightRatio);
  const face = faceStyle(system, zone.button);
  const base = pill
    ? 'rounded-full text-[0.55rem] font-bold tracking-wide'
    : system === 'nes'
      ? 'rounded-full text-sm font-black'
      : 'rounded-full text-sm font-bold';

  return (
    <div
      className={`flex h-full w-full items-center justify-center border shadow-inner transition-all ${base} ${
        active ? 'pad-pressed scale-95 brightness-125' : ''
      }`}
      style={{
        background: active ? face.activeBg : face.bg,
        borderColor: face.border,
        color: face.text,
        boxShadow: active ? `0 0 0 2px ${face.glow}` : `inset 0 -2px 0 ${face.inset}`,
      }}
    >
      {zone.label}
    </div>
  );
}

function faceStyle(system: SystemId, button?: PadButton): {
  bg: string;
  activeBg: string;
  border: string;
  text: string;
  glow: string;
  inset: string;
} {
  if (!button) {
    return {
      bg: 'rgba(0,0,0,0.35)',
      activeBg: 'rgba(74,222,128,0.7)',
      border: 'rgba(255,255,255,0.2)',
      text: 'rgba(255,255,255,0.8)',
      glow: 'rgba(74,222,128,0.5)',
      inset: 'rgba(0,0,0,0.25)',
    };
  }

  if (system === 'gb' || system === 'ws') {
    if (button === 'A' || button === 'B') {
      return {
        bg: system === 'ws' ? '#5a5040' : '#4a5a3a',
        activeBg: system === 'ws' ? '#e8dcc0' : '#9bbc0f',
        border: system === 'ws' ? '#3a3228' : '#2d3a24',
        text: system === 'ws' ? '#2a2418' : '#c4d6a0',
        glow: system === 'ws' ? '#e8dcc0' : '#9bbc0f',
        inset: system === 'ws' ? '#3a3228' : '#2d3a24',
      };
    }
    return {
      bg: system === 'ws' ? '#a89878' : '#6b7355',
      activeBg: system === 'ws' ? '#f0e6d0' : '#c4d6a0',
      border: system === 'ws' ? '#6a5a40' : '#3d4430',
      text: '#1a2010',
      glow: system === 'ws' ? '#f0e6d0' : '#c4d6a0',
      inset: system === 'ws' ? '#6a5a40' : '#3d4430',
    };
  }

  if (system === 'gba') {
    // AGB-001 のインディゴ筐体に、濃いグレーの A/B と肩ボタン。
    if (button === 'A' || button === 'B') {
      return {
        bg: '#3a3550',
        activeBg: '#b0a8d8',
        border: '#241f38',
        text: '#d8d3ee',
        glow: '#b0a8d8',
        inset: '#241f38',
      };
    }
    return {
      bg: '#4a4270',
      activeBg: '#c4bce4',
      border: '#2a2340',
      text: '#e8e4f6',
      glow: '#c4bce4',
      inset: '#2a2340',
    };
  }

  if (system === 'nes') {
    if (button === 'A' || button === 'B') {
      return {
        bg: '#b71c1c',
        activeBg: '#ef5350',
        border: '#7f0000',
        text: '#fff',
        glow: '#ef5350',
        inset: '#7f0000',
      };
    }
    return {
      bg: '#424242',
      activeBg: '#9e9e9e',
      border: '#212121',
      text: '#eee',
      glow: '#9e9e9e',
      inset: '#212121',
    };
  }

  if (system === 'snes') {
    const colors: Partial<Record<PadButton, { bg: string; active: string }>> = {
      X: { bg: '#5c6bc0', active: '#9fa8da' },
      Y: { bg: '#66bb6a', active: '#a5d6a7' },
      A: { bg: '#ef5350', active: '#ef9a9a' },
      B: { bg: '#ffee58', active: '#fff59d' },
      L: { bg: '#7e57c2', active: '#b39ddb' },
      R: { bg: '#7e57c2', active: '#b39ddb' },
    };
    const c = colors[button] ?? { bg: '#6a5a8a', active: '#b39ddb' };
    const darkText = button === 'B';
    return {
      bg: c.bg,
      activeBg: c.active,
      border: 'rgba(0,0,0,0.35)',
      text: darkText ? '#333' : '#fff',
      glow: c.active,
      inset: 'rgba(0,0,0,0.25)',
    };
  }

  // PS1 / PSP: △X ○A ×B □Y
  if (system !== 'psx' && system !== 'psp') {
    return {
      bg: 'rgba(0,0,0,0.35)',
      activeBg: 'rgba(74,222,128,0.7)',
      border: 'rgba(255,255,255,0.2)',
      text: 'rgba(255,255,255,0.8)',
      glow: 'rgba(74,222,128,0.5)',
      inset: 'rgba(0,0,0,0.25)',
    };
  }

  const ps: Partial<Record<PadButton, { bg: string; active: string; text: string }>> = {
    X: { bg: '#1b5e20', active: '#66bb6a', text: '#a5d6a7' },
    A: { bg: '#b71c1c', active: '#ef5350', text: '#ffcdd2' },
    B: { bg: '#0d47a1', active: '#42a5f5', text: '#bbdefb' },
    Y: { bg: '#880e4f', active: '#ec407a', text: '#f8bbd0' },
    L: { bg: '#37474f', active: '#90a4ae', text: '#eceff1' },
    R: { bg: '#37474f', active: '#90a4ae', text: '#eceff1' },
    L2: { bg: '#263238', active: '#78909c', text: '#eceff1' },
    R2: { bg: '#263238', active: '#78909c', text: '#eceff1' },
  };
  const c = ps[button] ?? { bg: '#424242', active: '#9e9e9e', text: '#eee' };
  return {
    bg: c.bg,
    activeBg: c.active,
    border: 'rgba(255,255,255,0.15)',
    text: c.text,
    glow: c.active,
    inset: 'rgba(0,0,0,0.4)',
  };
}

function DPadVisual({
  pressed,
  system,
  hardwareSkin,
}: {
  pressed: Set<PadButton>;
  system: SystemId;
  hardwareSkin: boolean;
}) {
  if (!hardwareSkin) {
    const on = (b: PadButton) => (pressed.has(b) ? 'bg-accent/70' : 'bg-white/10');
    return (
      <div className="relative h-full w-full">
        <div className="absolute inset-0 rounded-2xl border border-white/10 bg-black/45" />
        <div className={`absolute left-1/3 top-[6%] h-[28%] w-1/3 rounded-t-lg transition-colors ${on('UP')}`} />
        <div className={`absolute left-1/3 bottom-[6%] h-[28%] w-1/3 rounded-b-lg transition-colors ${on('DOWN')}`} />
        <div className={`absolute top-1/3 left-[6%] h-1/3 w-[28%] rounded-l-lg transition-colors ${on('LEFT')}`} />
        <div className={`absolute top-1/3 right-[6%] h-1/3 w-[28%] rounded-r-lg transition-colors ${on('RIGHT')}`} />
        <div className="absolute left-1/3 top-1/3 h-1/3 w-1/3 bg-white/15" />
      </div>
    );
  }

  const shell =
    system === 'gb'
      ? 'bg-[#3d4430] border-[#2d3a24]'
      : system === 'ws'
        ? 'bg-[#8a7a60] border-[#5a4a38]'
        : system === 'gba'
        ? 'bg-[#4a4270] border-[#2a2340]'
        : system === 'nes'
          ? 'bg-[#616161] border-[#424242]'
          : system === 'snes'
            ? 'bg-[#5c4d7a] border-[#3d3558]'
            : 'bg-[#263238] border-[#101416]';

  const on = (b: PadButton) => {
    const active = pressed.has(b);
    const color = active
      ? system === 'gb'
        ? 'bg-[#9bbc0f]'
        : system === 'ws'
          ? 'bg-[#f0e6d0]'
          : 'bg-white/55'
      : system === 'gb'
        ? 'bg-[#6b7355]'
        : system === 'ws'
          ? 'bg-[#c4b498]'
          : 'bg-black/35';
    return `${color}${active ? ' pad-pressed' : ''}`;
  };

  return (
    <div className={`relative h-full w-full rounded-xl border ${shell}`}>
      <div className={`absolute left-[34%] top-[5%] h-[30%] w-[32%] rounded-sm transition-colors ${on('UP')}`} />
      <div className={`absolute left-[34%] bottom-[5%] h-[30%] w-[32%] rounded-sm transition-colors ${on('DOWN')}`} />
      <div className={`absolute top-[34%] left-[5%] h-[32%] w-[30%] rounded-sm transition-colors ${on('LEFT')}`} />
      <div className={`absolute top-[34%] right-[5%] h-[32%] w-[30%] rounded-sm transition-colors ${on('RIGHT')}`} />
      <div className="absolute left-[34%] top-[34%] h-[32%] w-[32%] bg-black/20" />
    </div>
  );
}

function StickVisual({
  zone,
  stick,
  pressed,
}: {
  zone: ZoneDef;
  stick?: StickState | undefined;
  pressed: Set<PadButton>;
}) {
  const active = stick !== undefined;
  // ノブは中心から半径の 40% まで動かす。指の真下へ置くと指で隠れて見えない。
  const knobShift = 40;
  const dx = stick ? stick.x * knobShift : 0;
  const dy = stick ? stick.y * knobShift : 0;

  // 方向キー代わりのときは、いま入っている向きを縁の色で示す
  const dirActive =
    zone.asDpad === true &&
    (pressed.has('UP') || pressed.has('DOWN') || pressed.has('LEFT') || pressed.has('RIGHT'));

  return (
    <div
      data-stick-active={active ? 'true' : 'false'}
      className={`relative h-full w-full rounded-full border-2 bg-gradient-to-b from-white/15 to-black/40 shadow-inner transition-colors ${
        dirActive ? 'border-accent/70' : 'border-white/25'
      } ${zone.floating && !active ? 'opacity-45' : ''}`}
    >
      <div
        className={`absolute left-1/2 top-1/2 h-[55%] w-[55%] rounded-full border ${
          active ? 'pad-pressed border-accent bg-accent/60' : 'border-white/25 bg-white/10'
        }`}
        style={{
          // ノブは指に追従させる。transition を掛けると追従が遅れて
          // 「効いていない」と感じるので、離したときの戻りだけ滑らかにする。
          transform: `translate(calc(-50% + ${dx}%), calc(-50% + ${dy}%))`,
          transition: active ? 'none' : 'transform 120ms ease-out',
        }}
      />
    </div>
  );
}
